// ═══════════════════════════════════════════════════
// Sidebar — Your compliance command center
// Six channels. One scan. Real findings.
// ═══════════════════════════════════════════════════

import { NavLink, useLocation } from 'react-router';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HouseSimple,
  Buildings,
  MagnifyingGlass,
  ListChecks,
  Trophy,
  GearSix,
  CaretLeft,
  CaretRight,
  Lightning,
  SignOut,
  Scales,
} from '@phosphor-icons/react';
import { useUIStore } from '@/stores/ui';
import { useAuthStore } from '@/stores/auth';
import styles from './Sidebar.module.css';

const NAV_ITEMS = [
  { path: '/dashboard', label: 'Dashboard', icon: HouseSimple, description: 'The big picture' },
  { path: '/properties', label: 'Properties', icon: Buildings, description: 'Your portfolio' },
  { path: '/scans', label: 'Scans', icon: MagnifyingGlass, description: 'Scan history' },
  { path: '/findings', label: 'Findings', icon: ListChecks, description: 'What needs fixing' },
  { path: '/legislation', label: 'Legislation', icon: Scales, description: 'Law changes we watch' },
  { path: '/awards', label: 'Awards', icon: Trophy, description: 'Celebrate the wins' },
];

const BOTTOM_ITEMS = [
  { path: '/settings', label: 'Settings', icon: GearSix },
];

// Fun greetings based on time of day
function getGreeting(name: string): string {
  const hour = new Date().getHours();
  const firstName = name.split(' ')[0] || name;

  if (hour < 6) return `Burning the midnight oil, ${firstName}?`;
  if (hour < 9) return `Rise and comply, ${firstName} ☀️`;
  if (hour < 12) return `Good morning, ${firstName}`;
  if (hour < 14) return `Afternoon check-in, ${firstName}`;
  if (hour < 17) return `Keeping it compliant, ${firstName}`;
  if (hour < 20) return `Evening scan, ${firstName}?`;
  return `Late night compliance, ${firstName} 🌙`;
}

export function Sidebar() {
  const collapsed = useUIStore((s) => s.sidebarCollapsed);
  const toggle = useUIStore((s) => s.toggleSidebar);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const location = useLocation();

  return (
    <aside
      className={`${styles.sidebar} ${collapsed ? styles.collapsed : ''}`}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Logo */}
      <div className={styles.logoWrap}>
        <NavLink to="/dashboard" className={styles.logo} aria-label="Proppulse.ai home">
          <span className={styles.logoProp}>P</span>
          <AnimatePresence>
            {!collapsed && (
              <motion.span
                className={styles.logoText}
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
                transition={{ duration: 0.2 }}
              >
                <span className={styles.logoPropFull}>ROP</span>
                <span className={styles.logoPulse}>PULSE</span>
                <span className={styles.logoAi}>.AI</span>
              </motion.span>
            )}
          </AnimatePresence>
        </NavLink>
      </div>

      {/* Greeting */}
      <AnimatePresence>
        {!collapsed && user && (
          <motion.div
            className={styles.greeting}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <p className={styles.greetingText}>{getGreeting(user.name)}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* New Scan CTA */}
      <div className={styles.scanCta}>
        <NavLink to="/scans/new" className={`${styles.scanBtn} comet-border`} aria-label="Start a new property scan">
          <Lightning weight="bold" size={18} />
          <AnimatePresence>
            {!collapsed && (
              <motion.span
                initial={{ opacity: 0, width: 0 }}
                animate={{ opacity: 1, width: 'auto' }}
                exit={{ opacity: 0, width: 0 }}
              >
                New Scan
              </motion.span>
            )}
          </AnimatePresence>
        </NavLink>
      </div>

      {/* Main Nav */}
      <nav className={styles.nav}>
        <div className={styles.navSection}>
          {!collapsed && <span className={styles.navLabel}>Navigate</span>}
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname.startsWith(item.path);

            return (
              <NavLink
                key={item.path}
                to={item.path}
                className={`${styles.navItem} ${isActive ? styles.active : ''}`}
                title={collapsed ? item.label : undefined}
                aria-label={item.label}
              >
                <span className={styles.navIconWrap}>
                  <Icon
                    size={20}
                    weight={isActive ? 'fill' : 'regular'}
                    className={styles.navIcon}
                  />
                  {isActive && (
                    <motion.span
                      className={styles.activeIndicator}
                      layoutId="activeNav"
                      transition={{ type: 'spring', stiffness: 380, damping: 30 }}
                    />
                  )}
                </span>
                <AnimatePresence>
                  {!collapsed && (
                    <motion.span
                      className={styles.navText}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      {item.label}
                    </motion.span>
                  )}
                </AnimatePresence>
              </NavLink>
            );
          })}
        </div>
      </nav>

      {/* Bottom Section */}
      <div className={styles.bottom}>
        {BOTTOM_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = location.pathname.startsWith(item.path);
          return (
            <NavLink
              key={item.path}
              to={item.path}
              className={`${styles.navItem} ${isActive ? styles.active : ''}`}
              title={collapsed ? item.label : undefined}
              aria-label={item.label}
            >
              <Icon size={20} weight={isActive ? 'fill' : 'regular'} className={styles.navIcon} />
              {!collapsed && <span className={styles.navText}>{item.label}</span>}
            </NavLink>
          );
        })}

        <button
          className={styles.navItem}
          onClick={logout}
          title={collapsed ? 'Sign out' : undefined}
          aria-label="Sign out"
        >
          <SignOut size={20} weight="regular" className={styles.navIcon} />
          {!collapsed && <span className={styles.navText}>Sign Out</span>}
        </button>

        {/* Collapse Toggle */}
        <button
          className={styles.collapseBtn}
          onClick={toggle}
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <CaretRight size={16} /> : <CaretLeft size={16} />}
          {!collapsed && <span className={styles.navText}>Collapse</span>}
        </button>
      </div>
    </aside>
  );
}

